import { useState } from "react";
import { C, uid, fmt, Btn, Input, Select, Textarea, Modal, Section, StatCard, Tabs, useIsMobile, ESPECIES } from "./ui";

// Tipos de tratamiento (incluye productos de uso frecuente en Uruguay)
export const TIPOS_SANIDAD = [
  "Vacuna", "Antiparasitario", "Garrapaticida", "Mosquicida/Repelente",
  "Curabichera", "Antibiótico", "Vitamina/Mineral", "Medicamento", "Otro",
];
const ICON_SANIDAD = {
  Vacuna:"💉", Antiparasitario:"🔬", Garrapaticida:"🕷", "Mosquicida/Repelente":"🦟",
  Curabichera:"🩹", "Antibiótico":"💊", "Vitamina/Mineral":"🌿", Medicamento:"💊", Otro:"📋",
};
// Productos sugeridos por tipo (para el desplegable del stock)
const PRODUCTOS_SANIDAD = {
  "Vacuna":               ["Aftosa", "Brucelosis", "Carbunco", "Clostridial (Mancha/Gangrena)", "IBR/BVD", "Otra vacuna"],
  "Antiparasitario":      ["Ivermectina 1%", "Ivermectina 3.15%", "Doramectina", "Closantel", "Levamisol", "Ricobendazol", "Otro"],
  "Garrapaticida":        ["Amitraz", "Cipermetrina", "Fipronil", "Fluazurón", "Otro"],
  "Mosquicida/Repelente": ["Cipermetrina pour-on", "Deltametrina", "Otro"],
  "Curabichera":          ["Curabichera spray", "Cicatrizante con repelente", "Otro"],
  "Antibiótico":          ["Oxitetraciclina", "Penicilina", "Florfenicol", "Otro"],
  "Vitamina/Mineral":     ["Vitamina ADE", "Complejo B", "Hierro", "Selenio", "Otro"],
  "Medicamento":          ["Antiinflamatorio", "Antidiarreico", "Otro"],
  "Otro":                 ["Otro"],
};
const productosDe = (tipo) => PRODUCTOS_SANIDAD[tipo] || ["Otro"];

// ─── SELECTOR DE ANIMALES ─────────────────────────────────
function AnimalSelector({ potreros, selected, onChange }) {
  const [filtroP, setFiltroP] = useState("Todos");
  const [filtroE, setFiltroE] = useState("Todos");

  const animales = potreros
    .flatMap(p=>(p.animalesAsignados||[]).filter(a=>(a.estado||"activo")==="activo")
      .map(a=>({ id:a.id, caravana:a.caravana, categoria:a.categoria, especie:a.especie, potreroId:p.id, potreroNombre:p.nombre })))
    .filter(a=>filtroP==="Todos"||a.potreroId===filtroP)
    .filter(a=>filtroE==="Todos"||a.especie===filtroE);

  const selSet = new Set(selected.map(s=>s.animalId));

  const toggle = (a) => {
    if (selSet.has(a.id)) onChange(selected.filter(s=>s.animalId!==a.id));
    else onChange([...selected, { animalId:a.id, potreroId:a.potreroId }]);
  };

  const toggleAll = () => {
    if (animales.every(a=>selSet.has(a.id))) onChange([]);
    else onChange(animales.map(a=>({ animalId:a.id, potreroId:a.potreroId })));
  };

  return (
    <div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 12px" }}>
        <Select label="Filtrar potrero" value={filtroP} onChange={setFiltroP}
          options={[{v:"Todos",l:"Todos los potreros"},...potreros.map(p=>({v:p.id,l:p.nombre}))]}/>
        <Select label="Filtrar especie" value={filtroE} onChange={setFiltroE} options={["Todos",...ESPECIES]}/>
      </div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
        <div style={{ fontSize:11, color:C.muted }}>{selSet.size} seleccionado{selSet.size!==1?"s":""} de {animales.length}</div>
        <Btn size="sm" variant="ghost" onClick={toggleAll}>
          {animales.every(a=>selSet.has(a.id))&&animales.length>0 ? "Deseleccionar todos" : "Seleccionar todos"}
        </Btn>
      </div>
      {animales.length===0 ? (
        <div style={{ fontSize:12, color:C.muted, textAlign:"center", padding:"10px 0" }}>Sin animales activos con estos filtros</div>
      ) : (
        <div style={{ maxHeight:200, overflowY:"auto", border:`1px solid ${C.arenaDark}`, borderRadius:8 }}>
          {animales.map((a,i)=>(
            <div key={a.id} onClick={()=>toggle(a)}
              style={{ display:"flex", alignItems:"center", gap:10, padding:"7px 12px", cursor:"pointer",
                background:selSet.has(a.id)?C.pastoLight+"22":"transparent",
                borderBottom: i<animales.length-1?`1px solid ${C.arenaDark}`:"none" }}>
              <input type="checkbox" checked={selSet.has(a.id)} onChange={()=>toggle(a)} onClick={e=>e.stopPropagation()} style={{ cursor:"pointer" }}/>
              <span style={{ fontFamily:"monospace", fontSize:12, color:C.tierra }}>#{a.caravana||"—"}</span>
              <span style={{ fontSize:11, color:C.muted }}>{a.categoria}</span>
              <span style={{ fontSize:11, color:C.muted, marginLeft:"auto" }}>📍 {a.potreroNombre}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── MODAL APLICAR TRATAMIENTO ────────────────────────────
function ModalTratamiento({ potreros, stockSanidad, onAplicar, onCancel }) {
  const [tipo,      setTipo]      = useState(TIPOS_SANIDAD[0]);
  const [productoId,setProductoId]= useState("");
  const [fecha,     setFecha]     = useState(new Date().toISOString().slice(0,10));
  const [dosis,     setDosis]     = useState("");
  const [obs,       setObs]       = useState("");
  const [seleccion, setSeleccion] = useState([]);

  const productosStock = stockSanidad.filter(s=>s.tipo===tipo);
  const prod = stockSanidad.find(s=>s.id===productoId);

  // Cálculo: dosis × cantidad de animales vs stock disponible
  const nAnim = seleccion.length;
  const dosisN = parseFloat(dosis)||0;
  const necesario = dosisN * nAnim;
  const disponible = prod?.cantidad || 0;
  const alcanza = !prod || necesario <= disponible;

  const guardar = () => {
    if (!prod || nAnim===0 || !alcanza) return;
    const trat = { id:uid(), tipo, producto:prod.nombre, productoId:prod.id, fecha,
      dosis, unidad:prod.unidad, obs, total:necesario };
    onAplicar(trat, seleccion);
  };

  return (
    <Modal title="Aplicar tratamiento" onClose={onCancel} width={560}>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 12px" }}>
        <Select label="Tipo" value={tipo} onChange={v=>{setTipo(v);setProductoId("");}} options={TIPOS_SANIDAD}/>
        <Input label="Fecha" value={fecha} onChange={setFecha} type="date"/>
      </div>

      {productosStock.length>0 ? (
        <Select label="Producto (del stock)" value={productoId} onChange={setProductoId}
          options={[{v:"",l:"— Elegí producto —"}, ...productosStock.map(s=>({ v:s.id, l:`${s.nombre} · ${s.cantidad} ${s.unidad} en stock` }))]}/>
      ) : (
        <div style={{ background:"#FAE8E8", color:C.danger, borderRadius:8, padding:"10px 12px", fontSize:12.5, margin:"6px 0 14px", lineHeight:1.5 }}>
          ⚠ No tenés productos de tipo <b>{tipo}</b> en el stock. Cargalo en la pestaña <b>Stock</b> primero.
        </div>
      )}

      <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr", gap:"0 12px" }}>
        <Input label="Dosis por animal" value={dosis} onChange={setDosis} type="number" placeholder="Ej: 5"/>
        <div style={{ marginBottom:14 }}>
          <div style={{ fontSize:12, fontFamily:"monospace", textTransform:"uppercase", letterSpacing:"0.08em", color:C.muted, marginBottom:6 }}>Unidad</div>
          <div style={{ padding:"10px 12px", background:C.arena, borderRadius:8, fontSize:14, color:C.texto }}>
            {prod?.unidad || "—"}
          </div>
        </div>
      </div>

      <Textarea label="Observaciones" value={obs} onChange={setObs} rows={2}/>

      <div style={{ fontSize:11, fontFamily:"monospace", textTransform:"uppercase", letterSpacing:"0.08em", color:C.muted, marginBottom:8 }}>
        Animales a tratar <span style={{ color:C.danger }}>*</span>
      </div>
      <AnimalSelector potreros={potreros} selected={seleccion} onChange={setSeleccion}/>

      {/* Cálculo de consumo vs stock */}
      {prod && nAnim>0 && dosisN>0 && (
        <div style={{ marginTop:12, padding:"10px 13px", borderRadius:9,
          background: alcanza ? "#D4F0D8" : "#FAE8E8", border:`1px solid ${alcanza?"#90CC98":"#E09090"}`,
          fontSize:12.5, color: alcanza ? "#1A5228" : C.danger, lineHeight:1.6 }}>
          {dosis} {prod.unidad}/animal × {nAnim} animal{nAnim!==1?"es":""} = <b>{necesario.toLocaleString("es-UY")} {prod.unidad}</b>
          {" · "}Disponible: <b>{disponible.toLocaleString("es-UY")} {prod.unidad}</b>
          {!alcanza && <div style={{ fontWeight:700, marginTop:3 }}>⚠ No alcanza el stock. Reducí animales/dosis o cargá más.</div>}
        </div>
      )}

      <div style={{ display:"flex", gap:8, marginTop:16 }}>
        <Btn variant="ghost" onClick={onCancel} style={{flex:1}}>Cancelar</Btn>
        <Btn variant="primary" onClick={guardar} disabled={!prod||nAnim===0||!alcanza} style={{flex:1}}>
          Aplicar a {nAnim} animal{nAnim!==1?"es":""}
        </Btn>
      </div>
    </Modal>
  );
}

// ─── TAB TRATAMIENTOS ─────────────────────────────────────
function TabTratamientos({ potreros, stockSanidad, onAplicarTratamiento }) {
  const [showModal, setShowModal] = useState(false);
  const [expandido, setExpandido] = useState(null);

  const todosLosTrat = potreros
    .flatMap(p=>(p.animalesAsignados||[]).flatMap(a=>(a.sanidad||[]).map(t=>({
      ...t, animalCaravana:a.caravana, animalCategoria:a.categoria, potreroNombre:p.nombre
    }))));

  // Agrupar por aplicación (mismo producto + fecha + dosis)
  const gruposMap = {};
  todosLosTrat.forEach(t=>{
    const key = `${t.producto}|${t.fecha}|${t.tipo}|${t.dosis||""}|${t.unidad||""}`;
    if (!gruposMap[key]) gruposMap[key] = { key, producto:t.producto, tipo:t.tipo, fecha:t.fecha,
      dosis:t.dosis, unidad:t.unidad, obs:t.obs, animales:[] };
    gruposMap[key].animales.push({ caravana:t.animalCaravana, categoria:t.animalCategoria, potrero:t.potreroNombre });
  });
  const grupos = Object.values(gruposMap).sort((a,b)=>(b.fecha||"").localeCompare(a.fecha||""));

  const hoy = new Date().toISOString().slice(0,10);
  const esteMes = todosLosTrat.filter(t=>t.fecha?.slice(0,7)===hoy.slice(0,7));

  return (
    <div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8, marginBottom:16 }}>
        <StatCard val={grupos.length} label="aplicaciones"/>
        <StatCard val={esteMes.length} label="dosis este mes"/>
        <StatCard val={new Set(todosLosTrat.map(t=>t.animalCaravana)).size} label="animales tratados"/>
      </div>

      <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:14 }}>
        <Btn variant="primary" onClick={()=>setShowModal(true)} disabled={potreros.flatMap(p=>p.animalesAsignados||[]).length===0}>
          + Aplicar tratamiento
        </Btn>
      </div>

      {grupos.length===0 ? (
        <div style={{ textAlign:"center", padding:"40px 20px", color:C.muted }}>
          <div style={{ fontSize:36, marginBottom:12 }}>💉</div>
          <div style={{ fontSize:13 }}>Sin tratamientos registrados. Aplicá vacunas y demás desde aquí.</div>
        </div>
      ) : grupos.map(g=>{
        const open = expandido===g.key;
        return (
          <div key={g.key} style={{ background:C.white, borderRadius:9, marginBottom:6, border:`1.5px solid ${C.arenaDark}`, overflow:"hidden" }}>
            <div onClick={()=>setExpandido(open?null:g.key)} style={{ padding:"10px 14px", cursor:"pointer",
              display:"flex", justifyContent:"space-between", alignItems:"center", gap:10 }}>
              <div style={{ flex:1, minWidth:0 }}>
                <span style={{ fontSize:15, marginRight:6 }}>{ICON_SANIDAD[g.tipo]||"💊"}</span>
                <span style={{ fontWeight:600, fontSize:13, color:C.tierra }}>{g.producto}</span>
                <span style={{ fontSize:11, color:C.muted, marginLeft:8 }}>{g.tipo}</span>
                {g.dosis&&<span style={{ fontSize:11, color:C.muted, marginLeft:8 }}>{g.dosis} {g.unidad}/animal</span>}
                <div style={{ fontSize:11.5, color:C.pasto, fontWeight:600, marginTop:3 }}>
                  🐄 {g.animales.length} animal{g.animales.length!==1?"es":""} · {open?"ocultar":"ver detalle"}
                </div>
              </div>
              <div style={{ textAlign:"right", flexShrink:0 }}>
                <span style={{ fontFamily:"monospace", fontSize:11, color:C.muted }}>{fmt(g.fecha)}</span>
                <div style={{ fontSize:13, color:C.muted, marginTop:2 }}>{open?"▾":"▸"}</div>
              </div>
            </div>
            {open && (
              <div style={{ borderTop:`1px solid ${C.arena}`, padding:"8px 14px", background:C.fondo }}>
                {g.obs && <div style={{ fontSize:11, color:C.muted, fontStyle:"italic", marginBottom:6 }}>{g.obs}</div>}
                <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                  {g.animales.map((a,i)=>(
                    <span key={i} style={{ fontSize:11, background:C.white, border:`1px solid ${C.arenaDark}`,
                      borderRadius:6, padding:"4px 8px", fontFamily:"monospace", color:C.tierra }}>
                      #{a.caravana||"—"} · {a.categoria} · 📍{a.potrero}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}

      {showModal && (
        <ModalTratamiento potreros={potreros} stockSanidad={stockSanidad}
          onAplicar={(trat, sel)=>{ onAplicarTratamiento(trat, sel); setShowModal(false); }}
          onCancel={()=>setShowModal(false)}/>
      )}
    </div>
  );
}

// ─── TAB STOCK ────────────────────────────────────────────
function TabStock({ stockSanidad, onUpdate }) {
  const [show,        setShow]        = useState(false);
  const [tipo,        setTipo]        = useState(TIPOS_SANIDAD[0]);
  const [nombre,      setNombre]      = useState(productosDe(TIPOS_SANIDAD[0])[0]);
  const [cantidad,    setCantidad]    = useState("");
  const [unidad,      setUnidad]      = useState("ml");
  const [stockMin,    setStockMin]    = useState("");
  const [vencimiento, setVencimiento] = useState("");
  const [obs,         setObs]         = useState("");

  const hoy = new Date().toISOString().slice(0,10);

  const guardar = () => {
    onUpdate([...stockSanidad, { id:uid(), nombre, tipo, cantidad:parseFloat(cantidad)||0, unidad, stockMin:parseFloat(stockMin)||0, vencimiento, obs }]);
    setShow(false); setNombre(""); setCantidad(""); setStockMin(""); setVencimiento(""); setObs("");
  };

  const eliminar = (id) => onUpdate(stockSanidad.filter(s=>s.id!==id));

  const alertas = stockSanidad.filter(s=>(s.stockMin>0&&s.cantidad<s.stockMin)||(s.vencimiento&&s.vencimiento<hoy));

  return (
    <div>
      {alertas.length>0 && (
        <div style={{ background:"#FFF8E8", border:"1px solid #F0D060", borderRadius:9, padding:"10px 14px", marginBottom:14 }}>
          <div style={{ fontSize:12, fontWeight:600, color:C.warn, marginBottom:6 }}>⚠ {alertas.length} alerta{alertas.length!==1?"s":""} de stock</div>
          {alertas.map(a=>(
            <div key={a.id} style={{ fontSize:11, color:"#7A5800" }}>
              · {a.nombre}: {a.vencimiento&&a.vencimiento<hoy ? "vencido" : `stock bajo (${a.cantidad}/${a.stockMin} ${a.unidad})`}
            </div>
          ))}
        </div>
      )}

      <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:12 }}>
        <Btn size="sm" variant="primary" onClick={()=>setShow(true)}>+ Agregar producto</Btn>
      </div>

      {stockSanidad.length===0&&!show && (
        <div style={{ textAlign:"center", padding:"40px 20px", color:C.muted }}>
          <div style={{ fontSize:36, marginBottom:10 }}>💊</div>
          <div style={{ fontSize:13 }}>Registrá vacunas, antiparasitarios y medicamentos para controlar el stock.</div>
        </div>
      )}

      {stockSanidad.map(item=>{
        const bajo = item.stockMin>0&&item.cantidad<item.stockMin;
        const vencido = item.vencimiento&&item.vencimiento<hoy;
        return (
          <div key={item.id} style={{ background:bajo||vencido?"#FFF8E8":C.white, borderRadius:9, padding:"10px 14px", marginBottom:6,
            border:`1.5px solid ${bajo||vencido?"#F0D060":C.arenaDark}` }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
              <div>
                <span style={{ fontWeight:600, fontSize:13, color:C.tierra }}>{item.nombre}</span>
                <span style={{ fontSize:11, color:C.muted, marginLeft:8 }}>{item.tipo}</span>
                <div style={{ fontSize:12, color:C.texto, marginTop:3 }}>
                  {item.cantidad} {item.unidad}
                  {item.stockMin>0&&<span style={{ color:C.muted }}> · mín: {item.stockMin}</span>}
                  {item.vencimiento&&<span style={{ color:vencido?C.danger:C.muted }}> · vence: {fmt(item.vencimiento)}</span>}
                </div>
                {(bajo||vencido)&&<div style={{ fontSize:11, color:C.warn, marginTop:3 }}>
                  ⚠ {vencido?"Producto vencido":`Stock bajo mínimo`}
                </div>}
              </div>
              <Btn size="sm" variant="danger" onClick={()=>eliminar(item.id)}>×</Btn>
            </div>
          </div>
        );
      })}

      {show && (
        <div style={{ background:C.fondo, borderRadius:10, padding:16, border:`1px solid ${C.arenaDark}`, marginTop:8 }}>
          <div style={{ fontSize:13, fontWeight:600, color:C.tierra, marginBottom:12 }}>Nuevo producto</div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 12px" }}>
            <Select label="Tipo" value={tipo} onChange={v=>{setTipo(v);setNombre(productosDe(v)[0]);}} options={TIPOS_SANIDAD}/>
            <Select label="Producto" value={nombre} onChange={setNombre} options={productosDe(tipo)}/>
            <Input label="Cantidad" value={cantidad} onChange={setCantidad} type="number" placeholder="Ej: 500"/>
            <Select label="Unidad" value={unidad} onChange={setUnidad} options={["ml","L","mg","g","kg","dosis","comprimidos","unidad"]}/>
            <Input label="Stock mínimo" value={stockMin} onChange={setStockMin} type="number" placeholder="Alerta si baja de..."/>
            <Input label="Vencimiento" value={vencimiento} onChange={setVencimiento} type="date"/>
          </div>
          <Textarea label="Observaciones" value={obs} onChange={setObs} rows={2}/>
          <div style={{ display:"flex", gap:8 }}>
            <Btn variant="ghost" size="sm" onClick={()=>setShow(false)} style={{flex:1}}>Cancelar</Btn>
            <Btn variant="primary" size="sm" onClick={guardar} disabled={!nombre} style={{flex:1}}>Guardar</Btn>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── SANIDAD PANEL (exported) ─────────────────────────────
export function SanidadPanel({ potreros, stockSanidad, onAplicarTratamiento, onUpdateStock }) {
  const isMobile = useIsMobile();
  const [tab, setTab] = useState("tratamientos");
  const tabs = [{ id:"tratamientos", label:"Tratamientos" }, { id:"stock", label:"Stock" }];

  const hoy = new Date().toISOString().slice(0,10);
  const alertasStock = stockSanidad.filter(s=>(s.stockMin>0&&s.cantidad<s.stockMin)||(s.vencimiento&&s.vencimiento<hoy));
  const totalTrat = potreros.flatMap(p=>(p.animalesAsignados||[]).flatMap(a=>a.sanidad||[])).length;

  return (
    <div style={{ display:"flex", flexDirection: isMobile?"column":"row", flex:1, overflow:"hidden" }}>
      <aside style={{ width: isMobile?"100%":280, background:C.white,
        borderRight: isMobile?"none":`1px solid ${C.arenaDark}`,
        borderBottom: isMobile?`1px solid ${C.arenaDark}`:"none",
        overflowY:"auto", flexShrink:0, maxHeight: isMobile?"45%":"none" }}>
        <Section label="Resumen de sanidad">
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
            <StatCard val={totalTrat} label="tratamientos"/>
            <StatCard val={stockSanidad.length} label="productos en stock"/>
            <StatCard val={alertasStock.length} label="alertas stock"
              color={alertasStock.length>0?C.warn:C.ok}/>
            <StatCard val={potreros.flatMap(p=>p.animalesAsignados||[]).filter(a=>(a.sanidad||[]).length>0).length} label="animales tratados"/>
          </div>
        </Section>
        {alertasStock.length>0 && (
          <Section label="Alertas de stock">
            {alertasStock.map(s=>{
              const vencido=s.vencimiento&&s.vencimiento<hoy;
              return (
                <div key={s.id} style={{ background:"#FFF8E8", borderRadius:8, padding:"8px 12px", marginBottom:6 }}>
                  <div style={{ fontSize:12, fontWeight:600, color:C.warn }}>{s.nombre}</div>
                  <div style={{ fontSize:11, color:"#7A5800" }}>{vencido?"Vencido":`${s.cantidad}/${s.stockMin} ${s.unidad}`}</div>
                </div>
              );
            })}
          </Section>
        )}
      </aside>

      <div style={{ flex:1, overflowY:"auto", background:C.fondo, padding:"16px 20px" }}>
        <div style={{ fontFamily:"serif", fontSize:18, color:C.tierra, fontWeight:600, marginBottom:16 }}>Sanidad</div>
        <Tabs tabs={tabs} active={tab} onChange={setTab}/>
        {tab==="tratamientos" && <TabTratamientos potreros={potreros} stockSanidad={stockSanidad} onAplicarTratamiento={onAplicarTratamiento}/>}
        {tab==="stock" && <TabStock stockSanidad={stockSanidad} onUpdate={onUpdateStock}/>}
      </div>
    </div>
  );
}
